"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { fetchAPI } from "@/utils/fetchData";
import { sectionRenderer } from "@/utils/section-renderer";
import BottomCurves from "@/components/curves/bottomCurves";
import BannerFloatingArrow from "@/components/uiDecor/bannerFloatingArrow";
import purpleBG from "@/app/assets/purple-gradient-bg.svg";

const LANGUAGE = {
    ENGLISH: 'en',
    BAHASA: 'ms'
} as const;

type Language = typeof LANGUAGE[keyof typeof LANGUAGE];

export default function PrivacyPageClient() {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [currentLanguage, setCurrentLanguage] = useState<Language>(LANGUAGE.ENGLISH);

    useEffect(() => {
        const fetchDataAsync = async () => {
            try {
                const result = await fetchAPI("/privacy-page", "page");
                setData(result);
            } catch (error) {
                console.error("Error fetching privacy page data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDataAsync();
    }, []);

    const toggleLanguage = () => {
        setCurrentLanguage((prev: Language) => 
            prev === LANGUAGE.ENGLISH ? LANGUAGE.BAHASA : LANGUAGE.ENGLISH
        );
    };

    const getComponentIds = () => {
        if (!data?.data?.blocks) return { english: null, bahasa: null };
        
        const editorBlocks = data.data.blocks.filter((block: any) => 
            block.__component === "shared.editor"
        );
        
        return {
            english: editorBlocks[0]?.id || null,
            bahasa: editorBlocks[1]?.id || null
        };
    };

    const getFilteredBlocks = () => {
        if (!data?.data?.blocks) return [];
        
        const componentIds = getComponentIds();
        const targetComponentId = currentLanguage === LANGUAGE.ENGLISH 
            ? componentIds.english 
            : componentIds.bahasa;
        
        return data.data.blocks.filter((block: any) => {
            if (block.__component === "shared.editor") {
                return block.id === targetComponentId;
            }
            return true;
        });
    };

    const getPageTitle = () => {
        return currentLanguage === LANGUAGE.ENGLISH ? "Privacy Policy" : "Dasar Privasi";
    };

    const getButtonText = () => {
        return currentLanguage === LANGUAGE.BAHASA
            ? "Lihat versi Bahasa Inggeris bagi Dasar Privasi" 
            : "View the Bahasa Melayu translation of the Privacy Policy";
    };

    return (
        <>
            <section
                className={` px-0 py-20 sm:py-[8.333vw] flex flex-col justify-center min-h-[300px] md:min-h-[390px]`}
            >
                <Image
                    src={purpleBG}
                    width={1920}
                    height={1080}
                    alt="background image"
                    className="md:hidden absolute top-0 left-0 right-0 bottom-0 w-full h-full pointer-events-none -z-10 object-cover"
                    priority
                />

                <Image
                    src={purpleBG}
                    width={1920}
                    height={1080}
                    alt="background image"
                    className="hidden md:block absolute top-0 left-0 right-0 bottom-0 w-full h-full pointer-events-none -z-10 object-cover object-right md:object-center"
                    priority
                />

                <div className="z-[1] px-[10.625vw]">
                    <h1 className={`textChild opacity-100 md:w-[60%]`}>
                        {getPageTitle()}
                    </h1>
                    <div className="text-white font-light space-x-4 mt-4">
                        <button
                            onClick={toggleLanguage}
                            className="bg-purple hover:bg-purple/80 text-white px-4 py-2 rounded-lg transition-colors duration-200 font-medium"
                        >
                            {getButtonText()}
                        </button>
                    </div>
                </div>

                <BannerFloatingArrow
                    direction="left"
                    className="-bottom-4 md:-bottom-40 translate-y-14 md:translate-y-16 left-[10.625vw]"
                    name="announcements"
                />

                <BottomCurves color="dark grey" direction="right" />
            </section>
            {data &&
                getFilteredBlocks().map((section: any, index: number) =>
                    sectionRenderer(section, index, "/privacy")
                )}
        </>
    );
}
